import { BrandFollowData } from '../../@types/wishlist.js';
import { CookieHandler, CookieHandlerInstance } from './cookiehandler.js';
import { ADD_BRAND_EVENT, ADDED_BRAND_EVENT, config, REMOVE_BRAND_EVENT, REMOVED_BRAND_EVENT } from './config.js';
import { BrandApi, BrandApiInstance } from './brand-api.js';
import { trackAddToBrandlist, trackRemoveFromBrandlist } from './cube-tracking-layer.js';
import { getLoginTransition } from './login-state.js';
import { pushToSnackbar } from './snackbar-service.js';

export class BrandService {
  private readonly api: BrandApi;
  private readonly cookieHandler: CookieHandler;
  private readonly followedBrandCodes = new Set<string>();
  private readonly pendingBrandCodes = new Set<string>();
  private batchTimeout: number | undefined;

  private readonly onAddBrand: (_: Event) => void;
  private readonly onRemoveBrand: (_: Event) => void;

  constructor(api: BrandApi, cookieHandler: CookieHandler) {
    this.api = api;
    this.cookieHandler = cookieHandler;

    this.onAddBrand = (e: Event) => {
      const data = (e as CustomEvent<BrandFollowData>).detail;
      this.addBrand(data);
    };

    this.onRemoveBrand = (e: Event) => {
      const data = (e as CustomEvent<BrandFollowData>).detail;
      this.removeBrand(data);
    };
  }

  init() {
    document.addEventListener(ADD_BRAND_EVENT, this.onAddBrand);
    document.addEventListener(REMOVE_BRAND_EVENT, this.onRemoveBrand);
    this.handleMergeDetection();
  }

  destroy() {
    document.removeEventListener(ADD_BRAND_EVENT, this.onAddBrand);
    document.removeEventListener(REMOVE_BRAND_EVENT, this.onRemoveBrand);
    window.clearTimeout(this.batchTimeout);
  }

  private handleMergeDetection() {
    if (getLoginTransition(this.cookieHandler) === 'login') {
      this.api.mergeBrands();
    }
  }

  requestBrandState(brandCode: string) {
    this.pendingBrandCodes.add(brandCode);
    window.clearTimeout(this.batchTimeout);
    this.batchTimeout = window.setTimeout(() => {
      this.fetchBrandStates();
    }, 100);
  }

  private async fetchBrandStates() {
    const codes = [...this.pendingBrandCodes];
    this.pendingBrandCodes.clear();
    if (codes.length === 0) return;

    const followed = await this.api.containsBrands(codes);
    followed.forEach((brandCode) => {
      this.followedBrandCodes.add(brandCode);
      BrandService.dispatchAddedEvent({ brandCode });
    });
  }

  async addBrand(data: BrandFollowData, options?: { silent?: boolean }) {
    BrandService.dispatchAddedEvent(data);
    const count = await this.api.addBrand(data.brandCode);
    if (count === null) {
      BrandService.dispatchRemovedEvent(data);
      return;
    }
    this.followedBrandCodes.add(data.brandCode);
    trackAddToBrandlist(data.brandCode, data.brandName, count, data.tracking?.placement);
    // the undo of an unfollow re-follows silently; the success snackbar is only meant for an explicit
    // heart add (see ENTS-10819). The recommendation slider skips it too - the brand appears directly
    // in the followed list right above the slider, so the toast would be redundant.
    if (!options?.silent && data.tracking?.placement !== 'brand_recommendation_slider') {
      this.showSnackbar();
    }
  }

  async removeBrand(data: BrandFollowData) {
    BrandService.dispatchRemovedEvent(data);
    const count = await this.api.removeBrand(data.brandCode);
    if (count === null) {
      BrandService.dispatchAddedEvent(data);
      return;
    }
    this.followedBrandCodes.delete(data.brandCode);
    trackRemoveFromBrandlist(data.brandCode, data.brandName, count, data.tracking?.placement, data.tracking?.addDate);
  }

  private showSnackbar() {
    const message = config.messageBrandFollowSnackbar;
    if (!message) return;
    pushToSnackbar(message);
  }

  private static dispatchAddedEvent(data: BrandFollowData) {
    document.dispatchEvent(new CustomEvent(ADDED_BRAND_EVENT, { detail: data }));
  }

  private static dispatchRemovedEvent(data: BrandFollowData) {
    document.dispatchEvent(new CustomEvent(REMOVED_BRAND_EVENT, { detail: data }));
  }
}

export const brandService = new BrandService(BrandApiInstance, CookieHandlerInstance);
